import { differenceInCalendarDays, format, isValid, parseISO } from 'date-fns';
import type { Contract, Transaction } from '../types/data';

export type AppNotificationSeverity = 'info' | 'warning' | 'urgent';

export interface AppNotification {
  id: string;
  title: string;
  message: string;
  severity: AppNotificationSeverity;
  href: string;
  createdAt: string;
  badgeLabel: string;
  category: 'contract' | 'finance' | 'mentor';
}

function parseSafeDate(value?: string) {
  if (!value) {
    return null;
  }

  const parsed = parseISO(value);
  return isValid(parsed) ? parsed : null;
}

function getSeverityWeight(severity: AppNotificationSeverity) {
  if (severity === 'urgent') return 0;
  if (severity === 'warning') return 1;
  return 2;
}

function buildContractNotifications(contracts: Contract[], today: Date): AppNotification[] {
  return contracts.flatMap((contract) => {
    if (contract.status === 'Concluído') {
      return [];
    }

    const endDate = parseSafeDate(contract.endDate);
    if (!endDate) {
      return [];
    }

    const daysLeft = differenceInCalendarDays(endDate, today);
    if (daysLeft < 0 || daysLeft > 7) {
      return [];
    }

    const severity: AppNotificationSeverity = daysLeft <= 2 ? 'urgent' : 'warning';
    const whenLabel =
      daysLeft === 0
        ? 'vence hoje'
        : daysLeft === 1
          ? 'vence amanhã'
          : `vence em ${daysLeft} dias`;

    return [
      {
        id: `contract-deadline-${contract.id}`,
        title: `Prazo próximo: ${contract.number}`,
        message: `${contract.address} ${whenLabel}.`,
        severity,
        href: `/contratos/${contract.id}`,
        createdAt: contract.endDate,
        badgeLabel: daysLeft <= 2 ? 'Urgente' : 'Prazo próximo',
        category: 'contract',
      },
    ];
  });
}

function buildTransactionNotifications(transactions: Transaction[], contracts: Contract[], today: Date): AppNotification[] {
  return transactions.flatMap((transaction) => {
    const isPending = transaction.status === 'A Pagar' || transaction.status === 'A Receber';
    if (!isPending) {
      return [];
    }

    const transactionDate = parseSafeDate(transaction.date);
    if (!transactionDate) {
      return [];
    }

    const daysToDue = differenceInCalendarDays(transactionDate, today);
    if (daysToDue > 3) {
      return [];
    }

    const relatedContract = contracts.find((contract) => contract.id === transaction.contractId);
    const targetLabel = transaction.type === 'income' ? 'Recebimento' : 'Pagamento';
    const severity: AppNotificationSeverity = daysToDue < 0 ? 'urgent' : 'warning';

    const dueLabel =
      daysToDue < 0
        ? `está vencido há ${Math.abs(daysToDue)} dia${Math.abs(daysToDue) === 1 ? '' : 's'}`
        : daysToDue === 0
          ? 'vence hoje'
          : daysToDue === 1
            ? 'vence amanhã'
            : `vence em ${daysToDue} dias`;

    return [
      {
        id: `transaction-deadline-${transaction.id}`,
        title: `${targetLabel} pendente`,
        message: `${transaction.description} ${dueLabel}${relatedContract ? ` no contrato ${relatedContract.number}` : ''}.`,
        severity,
        href: transaction.contractId ? `/financeiro?contractId=${transaction.contractId}` : '/financeiro',
        createdAt: transaction.date,
        badgeLabel: daysToDue < 0 ? 'Vencido' : 'Financeiro',
        category: 'finance',
      },
    ];
  });
}

export function sortNotifications(notifications: AppNotification[]) {
  return [...notifications].sort(
    (left, right) => {
      const severityDelta = getSeverityWeight(left.severity) - getSeverityWeight(right.severity);
      if (severityDelta !== 0) {
        return severityDelta;
      }

      const leftDate = parseSafeDate(left.createdAt);
      const rightDate = parseSafeDate(right.createdAt);
      if (!leftDate || !rightDate) {
        return 0;
      }

      return leftDate.getTime() - rightDate.getTime();
    },
  );
}

export function buildNotifications(
  contracts: Contract[],
  transactions: Transaction[],
  mentorNotifications: AppNotification[] = [],
  today = new Date(),
): AppNotification[] {
  return sortNotifications([
    ...buildContractNotifications(contracts, today),
    ...buildTransactionNotifications(transactions, contracts, today),
    ...mentorNotifications,
  ]);
}

export function formatNotificationDate(value: string) {
  const parsed = parseSafeDate(value);
  return parsed ? format(parsed, 'dd/MM/yyyy') : '-';
}
